/**
 * ClassPrintView
 *
 * Full-screen overlay that shows a print-ready class list.
 * - One class per page (CSS page-break-after)
 * - Students numbered and sorted by last name
 * - "Print / Save as PDF" button opens a new blank window containing only
 *   the serialised class pages, then calls window.print() there — so no
 *   app chrome ever appears in the print output.
 * - "Close" button dismisses the overlay
 *
 * Receives already-loaded client-side data (no extra API call needed).
 */
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer } from 'lucide-react';
import { Button } from '../ui/button';

// ── Single class page ───────────────────────────────────────────────────────
function ClassPage({ classGroup, students, isLast }) {
  const clasStudents = students
    .filter((s) => classGroup.students.includes(s.uuid))
    .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));

  return (
    <div
      style={{
        pageBreakAfter: isLast ? 'auto' : 'always',
        breakAfter: isLast ? 'auto' : 'page',
        padding: '24px 32px',
        minHeight: '100%',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Class header */}
      <div style={{ borderBottom: '2px solid #E5E7EB', paddingBottom: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>
              {classGroup.name}
            </h2>
            {classGroup.teacher_name && (
              <span style={{ fontSize: 12, color: '#374151' }}>
                <strong>Múinteoir:</strong> {classGroup.teacher_name}
              </span>
            )}
          </div>
          <div style={{ textAlign: 'right', fontSize: 14, fontWeight: 600, color: '#374151' }}>
            {clasStudents.length} mac léinn
          </div>
        </div>
      </div>

      {/* Student table */}
      {clasStudents.length === 0 ? (
        <p style={{ color: '#9CA3AF', fontSize: 12, fontStyle: 'italic' }}>Níl aon mhac léinn sa rang seo fós.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
              <th style={{ padding: '4px 8px', fontSize: 11, color: '#6B7280', textAlign: 'left', fontWeight: 600, width: 32 }}>
                #
              </th>
              <th style={{ padding: '4px 8px', fontSize: 11, color: '#6B7280', textAlign: 'left', fontWeight: 600, width: 80 }}>
                ID
              </th>
              <th style={{ padding: '4px 8px', fontSize: 11, color: '#6B7280', textAlign: 'left', fontWeight: 600 }}>
                Ainm
              </th>
              <th style={{ padding: '4px 8px', fontSize: 11, color: '#6B7280', textAlign: 'left', fontWeight: 600, width: 90 }}>
                Bliain
              </th>
              <th style={{ padding: '4px 8px', fontSize: 11, color: '#6B7280', textAlign: 'left', fontWeight: 600, width: 110 }}>
                Teach
              </th>
            </tr>
          </thead>
          <tbody>
            {clasStudents.map((student, idx) => (
              <tr
                key={student.uuid}
                style={{ borderBottom: '1px solid #F3F4F6' }}
              >
                <td style={{ padding: '4px 8px', fontSize: 11, color: '#9CA3AF', textAlign: 'right' }}>
                  {idx + 1}
                </td>
                <td style={{ padding: '4px 8px', fontSize: 11, color: '#374151', fontFamily: 'monospace' }}>
                  {student.student_id}
                </td>
                <td style={{ padding: '4px 8px', fontSize: 12, fontWeight: 500, color: '#111827' }}>
                  {student.last_name}, {student.first_name}
                </td>
                <td style={{ padding: '4px 8px', fontSize: 11, color: '#6B7280' }}>
                  {student.school_year}
                </td>
                <td style={{ padding: '4px 8px', fontSize: 11, color: '#6B7280' }}>
                  {student.house_name}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export function ClassPrintView({ course, classes, students, onClose }) {
  const scrollRef = useRef(null);

  const generatedAt = new Date().toLocaleString('ga-IE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  // Regular classes first, cinnirí class last; skip empty classes
  const printableClasses = [
    ...classes.filter((cg) => !cg.is_cinnire_class && cg.current_enrollment > 0),
    ...classes.filter((cg) =>  cg.is_cinnire_class && cg.current_enrollment > 0),
  ];

  const totalStudents = printableClasses.reduce((n, cg) => n + cg.current_enrollment, 0);

  // Prevent body scroll while the overlay is open.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handlePrint = () => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) { window.print(); return; }

    const bodyHtml = scrollEl.innerHTML;
    const title = course?.name ?? 'Ranganna';

    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) { window.print(); return; }

    win.document.write(`<!DOCTYPE html>
<html lang="ga">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0; padding: 0;
      font-family: system-ui, -apple-system, sans-serif;
      background: white;
    }
    .class-card {
      box-shadow: none !important;
      border-radius: 0 !important;
      margin-bottom: 0 !important;
      overflow: visible !important;
    }
  </style>
</head>
<body>
${bodyHtml}
<script>window.print();<\/script>
</body>
</html>`);
    win.document.close();
  };

  return createPortal(
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          backgroundColor: '#f3f4f6',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 20px',
            backgroundColor: '#1F2937',
            color: 'white',
            flexShrink: 0,
          }}
        >
          <div>
            <span style={{ fontWeight: 600, fontSize: 15 }}>{course?.name}</span>
            <span style={{ fontSize: 12, color: '#9CA3AF', marginLeft: 12 }}>
              {printableClasses.length} rang · {totalStudents} mac léinn
            </span>
            <span style={{ fontSize: 11, color: '#6B7280', marginLeft: 12 }}>{generatedAt}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-1.5" />
              Priontáil / Save as PDF
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onClose}
              style={{ color: 'white', borderColor: '#4B5563', backgroundColor: 'transparent' }}
            >
              <X className="h-4 w-4 mr-1" />
              Dún
            </Button>
          </div>
        </div>

        {/* Scrollable preview */}
        <div
          ref={scrollRef}
          style={{ flex: 1, overflowY: 'auto', padding: 24 }}
        >
          {printableClasses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#6B7280' }}>
              Níl aon mhac léinn sannta do ranganna fós.
            </div>
          ) : (
            printableClasses.map((cg, idx) => (
              <div
                key={cg.uuid}
                className="class-card"
                style={{
                  backgroundColor: 'white',
                  borderRadius: 8,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                  marginBottom: idx < printableClasses.length - 1 ? 24 : 0,
                }}
              >
                <ClassPage
                  classGroup={cg}
                  students={students}
                  isLast={idx === printableClasses.length - 1}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
